import React from "react";
import { Button } from "@/components/ui/button";
import CarImage from "@/assets/illustrations/OriginalCarImageIcon.svg";
import i18n from "@/lang/i18n";
import { CheckInRecord } from "@/central-atoms/everest-fleet-pwa/responses";
import EditCardValuesDialog from "@/components/custom/EditCardValuesDialog";
import { z } from "zod";
import { isNumber } from "lodash";
import { getNumber } from "@/helpers";
import { useNavigate } from "react-router-dom";

const odometerSchema = z.object({
    odometer: z.coerce.number().min(0, "Odometer cannot be less than 0"),
});

const socSchema = z.object({
    soc: z.coerce.number().min(0, "Soc cannot be less than 0").max(100, "Soc cannot be more than 100"),
});

interface CheckInCardProps {
    checkInRecord: CheckInRecord;
    onConfirm: () => void;
    setOdo: (value: number) => void;
    setSoc: (value: number) => void;
    odo: number;
    soc: number;
    checkInsLeft: number;
    loading: boolean
}

const CheckInCard: React.FC<CheckInCardProps> = ({
    checkInRecord,
    onConfirm,
    setOdo,
    setSoc,
    odo,
    soc,
    checkInsLeft,
    loading
}) => {

    const navigate = useNavigate();

    if(!checkInRecord) {
        navigate("/check-in");
    }

    return (
        <div className="min-h-screen w-full flex items-center justify-center px-3 relative">
            {/* White Card */}
            <div className="bg-white rounded-[20px] shadow-lg w-full max-w-md p-6">
                {/* Title Section */}
                <div className="flex justify-center items-center mb-4">
                    <h2 className="text-xl font-semibold mb-1 mr-2">
                        {i18n.t("check_in_details")}
                    </h2>
                    <p className="text-sm text-primary">
                        ({checkInsLeft} left)
                    </p>
                </div>

                {/* Hub Name */}
                <p className="text-sm text-center text-primary mb-2">
                    {checkInRecord.hub}
                </p>

                {/* Car Image */}
                <div className="flex items-center justify-center mb-4">
                    <img src={CarImage} alt="Car" className="w-52 mb-2" />
                </div>

                {/* Time Since Allotted */}
                {checkInRecord.track_props?.time_since_allotment && <div className="text-center text-gray-600 text-sm mb-4">
                    {i18n.t("time_since_allotted")}{" "}
                    <div className="text-primary text-sm font-semibold text-center">
                        {checkInRecord.track_props?.time_since_allotment || ""}
                    </div>
                </div>}

                {/* Vehicle Number & Pilot Name in a horizontal row */}
                <div className="grid grid-cols-2 gap-4 text-start mb-4">
                    <div className="flex flex-col items-start">
                        <p className="text-gray-500 text-xs font-medium mb-1">
                            {i18n.t("vehicle_number")}
                        </p>
                        <p className="text-primary text-sm font-semibold">
                            {checkInRecord.track_props?.car_number}
                        </p>
                    </div>
                    <div className="flex flex-col items-start">
                        <p className="text-gray-500 text-xs font-medium mb-1">
                            {i18n.t("pilot_name")}
                        </p>
                        <p className="text-primary text-sm font-semibold">
                            {checkInRecord.name}
                        </p>
                    </div>
                </div>

                {/* Odometer & SOC Grid */}
                <div className="grid grid-cols-2 gap-4 text-start mb-4">
                    <div className="flex flex-col items-start">
                        <div className="flex items-center">
                            <p className="text-gray-500 text-xs font-medium">
                                {i18n.t("pilot_odometer")}
                            </p>
                            <EditCardValuesDialog
                                disabled={false}
                                handleSubmit={(data) => setOdo(data.odometer)}
                                placeholder={i18n.t("enter_odometer")}
                                formSchema={odometerSchema}
                                fieldType="number"
                                fieldName="odometer"
                                defaultValue={odo?.toString()}
                                title={i18n.t("odometer")}
                                inputPrependText={i18n.t("km")}
                            />
                        </div>
                        <p className="text-primary text-sm">{odo.toFixed(0)} {i18n.t('km')}</p>
                    </div>
                    <div className="flex flex-col items-start">

                        <div className="flex items-center">
                            <p className="text-gray-500 text-xs font-medium">
                                {i18n.t("pilot_soc")}
                            </p>

                            <EditCardValuesDialog
                                disabled={false}
                                handleSubmit={(data) => setSoc(data.soc)}
                                placeholder={i18n.t("soc")}
                                formSchema={socSchema}
                                fieldType="number"
                                fieldName="soc"
                                defaultValue={soc?.toString()}
                                title={i18n.t("soc")}
                                inputPrependText="%"
                            />
                        </div>
                        <p className="text-primary text-sm">{soc.toFixed(0)} {i18n.t('%')}</p>
                    </div>
                </div>

                {/* Additional Info Grid */}
                <div className="grid grid-cols-2 gap-4 text-start mb-4">
                    <div className="flex flex-col items-start">
                        <p className="text-gray-500 text-xs font-medium mb-1">
                            {i18n.t("total_run")}
                        </p>
                        <p className="text-primary text-sm font-semibold">
                            {getNumber(checkInRecord.track_props?.total_kms_driven, i18n.t("km"))}
                        </p>
                    </div>
                    <div className="flex flex-col items-start">
                        <p className="text-gray-500 text-xs font-medium mb-1">
                            {i18n.t("total_energy_consumed")}
                        </p>
                        <p className="text-primary text-sm font-semibold">
                            {getNumber(checkInRecord.track_props?.total_units_used, i18n.t("kWh"))}
                        </p>
                    </div>
                    <div className="flex flex-col items-start">
                        <p className="text-gray-500 text-xs font-medium mb-1">
                            {i18n.t("can_validity")}
                        </p>
                        <p className="text-primary text-sm font-semibold">
                            {getNumber(checkInRecord.track_props?.success_rate)}
                        </p>
                    </div>
                    <div className="flex flex-col items-start">
                        <p className="text-gray-500 text-xs font-medium mb-1">
                            {i18n.t("efficiency_score")}
                        </p>
                        <p className="text-primary text-sm font-semibold">
                            {getNumber(checkInRecord.track_props?.driver_efficiency)}
                        </p>
                    </div>
                    <div className="flex flex-col items-start col-span-2">
                        <p className="text-gray-500 text-xs font-medium mb-1">
                            {i18n.t("avg_km_per_day")}
                        </p>
                        <p className="text-primary text-sm font-semibold">
                            {getNumber(checkInRecord.track_props?.average_kms_driven, i18n.t("km"))}
                        </p>
                    </div>
                </div>

                {/* Confirm Button */}
                <Button
                    disabled={loading}
                    onClick={onConfirm}
                    className="w-full py-3 text-sm font-semibold text-white bg-primary hover:bg-primary-dark"
                >
                    {i18n.t("check_in")}
                </Button>
            </div>
        </div>
    );
};

export default CheckInCard;
